import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { useTheme } from "next-themes";
import { cn } from "@repo/blacksmith-ui/cn";
import { assetUrl } from "@/lib/assetUrl";
import { useAnvilActiveTransition } from "./useAnvilActiveTransition";
import { useTickerTransition } from "./useTickerTransition";
import { ANIMATION_VARIANTS, ANVIL_ANIMATION_ASSETS, anvilAnimationFile, seededAnvilActiveVariant, seededIndex, type AnvilActiveVariant, type AnvilAnimationVariant, type AnvilTheme } from "@repo/blacksmith-ui/anvil/animations";
export { ANIMATION_VARIANTS, ANVIL_ANIMATION_ASSETS, seededAnvilActiveVariant, type AnvilActiveVariant };
const STATUS_WORDS = ["Thinking", "Pondering", "Forging", "Hammering", "Shaping", "Drafting", "Analyzing", "Synthesizing", "Constructing", "Assembling", "Conjuring", "Refining", "Brewing", "Sculpting", "Weaving", "Polishing", "Tinkering", "Calibrating", "Designing", "Iterating", "Compiling", "Computing", "Deciphering", "Evaluating", "Exploring", "Formulating", "Investigating", "Mulling", "Processing", "Reasoning", "Strategizing", "Contemplating", "Deliberating", "Devising", "Envisioning", "Imagining", "Mapping", "Orchestrating", "Plotting", "Preparing", "Puzzling", "Ruminating", "Scheming", "Smelting", "Sparking", "Tempering", "Welding", "Wiring", "Crunching", "Hatching"] as const;

/** The turn's placeholder status word, shown until a live activity label arrives. */
export function seededStatusWord(seed: string): string {
  return STATUS_WORDS[seededIndex(`${seed}:word`, STATUS_WORDS.length)]!;
}

/** Versioned dashboard URL for an anvil animation; see {@link anvilAnimationFile}. */
export function anvilAnimationSrc(animationVariant: AnvilAnimationVariant, activeVariant: AnvilActiveVariant, themeSuffix: AnvilTheme): string {
  return assetUrl(`anvil-animations/${anvilAnimationFile(animationVariant, activeVariant, themeSuffix)}`);
}
export function AnvilWorkingStatus(t0) {
  const $ = _c(34);
  const {
    seed,
    animationVariant: t1,
    pickedVariant,
    showLabel: t2,
    animateIn: t3,
    animateOut: t4,
    statusLabel,
    labelExtra,
    labelToggle
  } = t0;
  const animationVariant = t1 === undefined ? "active" : t1;
  const showLabel = t2 === undefined ? true : t2;
  const animateIn = t3 === undefined ? true : t3;
  const animateOut = t4 === undefined ? false : t4;
  const {
    resolvedTheme
  } = useTheme();
  const [randomVariant] = useState(_temp);
  const [randomWord] = useState(_temp2);
  const hasSeed = seed !== undefined;
  let t5;
  if ($[0] !== hasSeed || $[1] !== pickedVariant || $[2] !== randomVariant || $[3] !== seed) {
    t5 = pickedVariant ?? (hasSeed ? seededAnvilActiveVariant(seed) : randomVariant);
    $[0] = hasSeed;
    $[1] = pickedVariant;
    $[2] = randomVariant;
    $[3] = seed;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const activeVariant = t5;
  let t6;
  if ($[5] !== hasSeed || $[6] !== randomWord || $[7] !== seed) {
    t6 = hasSeed ? seededStatusWord(seed) : randomWord;
    $[5] = hasSeed;
    $[6] = randomWord;
    $[7] = seed;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const seededWord = t6;
  let t7;
  if ($[9] !== seededWord || $[10] !== statusLabel) {
    t7 = statusLabel?.trim() || seededWord || "Thinking";
    $[9] = seededWord;
    $[10] = statusLabel;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const word = t7;
  const suffix = resolvedTheme === "dark" ? "dark" : "light";
  const {
    src: transitionSrc,
    holding
  } = useAnvilActiveTransition(animationVariant === "active" ? activeVariant : null, suffix);
  const [heldWord, setHeldWord] = useState(word);
  if (!holding && heldWord !== word) {
    setHeldWord(word);
  }
  const shownWord = holding ? heldWord : word;
  let t8;
  if ($[12] !== activeVariant || $[13] !== animationVariant || $[14] !== suffix || $[15] !== transitionSrc) {
    t8 = transitionSrc ?? anvilAnimationSrc(animationVariant, activeVariant, suffix);
    $[12] = activeVariant;
    $[13] = animationVariant;
    $[14] = suffix;
    $[15] = transitionSrc;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const src = t8;
  let t9;
  if ($[17] !== shownWord) {
    t9 = <RollingLabel text={shownWord} className="type-body-s-regular text-neutral-900 dark:text-neutral-100" />;
    $[17] = shownWord;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== labelExtra || $[20] !== t9) {
    t10 = <>{t9}{labelExtra}</>;
    $[19] = labelExtra;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const label = t10;
  const t11 = animateOut ? "animate-anvil-exit" : animateIn && "animate-anvil-enter";
  let t12;
  if ($[22] !== t11) {
    t12 = cn("flex select-none items-center gap-1.5 py-1", t11);
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== src) {
    t13 = <img src={src} alt="" width={40} height={40} draggable={false} />;
    $[24] = src;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== label || $[27] !== labelToggle || $[28] !== showLabel) {
    t14 = showLabel && labelToggle ? <button type="button" onClick={labelToggle.onToggle} aria-expanded={labelToggle.expanded} data-testid="anvil-label-toggle" className="flex cursor-pointer items-center gap-1.5 rounded-sm hover:opacity-80">{label}</button> : showLabel ? label : null;
    $[26] = label;
    $[27] = labelToggle;
    $[28] = showLabel;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t12 || $[31] !== t13 || $[32] !== t14) {
    t15 = <div className={t12}>{t13}{t14}</div>;
    $[30] = t12;
    $[31] = t13;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  return t15;
}

/**
 * Status line that rolls to a new label: both labels travel a full line in
 * lockstep through a vertical clip (`label-ticker-*` in globals.css). The
 * first label renders as-is; only changes animate.
 */
function _temp2() {
  return STATUS_WORDS[Math.floor(Math.random() * STATUS_WORDS.length)];
}
function _temp() {
  return ANIMATION_VARIANTS[Math.floor(Math.random() * ANIMATION_VARIANTS.length)];
}
export function RollingLabel(t0) {
  const $ = _c(17);
  const {
    text,
    className,
    sizeToCurrent: t1
  } = t0;
  const sizeToCurrent = t1 === undefined ? false : t1;
  const {
    current,
    previous
  } = useTickerTransition(text);
  const t2 = sizeToCurrent && "relative min-w-0 grid-cols-[minmax(0,1fr)]";
  let t3;
  if ($[0] !== className || $[1] !== t2) {
    t3 = cn("inline-grid overflow-y-clip [--label-ticker-travel:100%]", t2, className);
    $[0] = className;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== previous || $[4] !== sizeToCurrent) {
    t4 = previous !== null && <span key={`out:${previous}`} aria-hidden={true} className={cn(sizeToCurrent ? "absolute top-0 left-0" : "[grid-area:1/1]", "label-ticker-exit")}>{previous}</span>;
    $[3] = previous;
    $[4] = sizeToCurrent;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = `in:${current}`;
  const t6 = sizeToCurrent && "truncate";
  const t7 = previous !== null && "label-ticker-enter";
  let t8;
  if ($[6] !== t6 || $[7] !== t7) {
    t8 = cn("[grid-area:1/1]", t6, t7);
    $[6] = t6;
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== current || $[10] !== t5 || $[11] !== t8) {
    t9 = <span key={t5} className={t8}>{current}</span>;
    $[9] = current;
    $[10] = t5;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t3 || $[14] !== t4 || $[15] !== t9) {
    t10 = <span className={t3}>{t4}{t9}</span>;
    $[13] = t3;
    $[14] = t4;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  return t10;
}